// Kas & Bank — E-Statement API helpers (2026-09-23).
import api from "@/lib/api";

/** Convert File → base64 (raw payload tanpa data:URL prefix). */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || "");
      const b = s.includes(",") ? s.split(",")[1] : s;
      resolve(b);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function previewEstatement(fileB64, delimiter = ",") {
  const { data } = await api.post("/kas-bank/estatements/preview", { file_b64: fileB64, delimiter });
  return data;
}

export async function createEstatement(payload) {
  const { data } = await api.post("/kas-bank/estatements", payload);
  return data;
}

export async function listEstatements(params = {}) {
  const q = new URLSearchParams();
  if (params.account_id) q.set("account_id", params.account_id);
  if (params.period_month) q.set("period_month", params.period_month);
  const { data } = await api.get(`/kas-bank/estatements${q.toString() ? "?" + q : ""}`);
  return Array.isArray(data) ? data : [];
}

export async function deleteEstatement(estId) {
  const { data } = await api.delete(`/kas-bank/estatements/${encodeURIComponent(estId)}`);
  return data;
}

export async function listEstatementRows(estId) {
  const { data } = await api.get(`/kas-bank/estatements/${encodeURIComponent(estId)}/rows`);
  return Array.isArray(data) ? data : [];
}

export async function listAllRows(params = {}) {
  const q = new URLSearchParams();
  if (params.account_id) q.set("account_id", params.account_id);
  if (params.only_unbreakdown) q.set("only_unbreakdown", "true");
  const { data } = await api.get(`/kas-bank/estatements/rows/all${q.toString() ? "?" + q : ""}`);
  return Array.isArray(data) ? data : [];
}

export async function breakdownRow(rowId, payload) {
  const { data } = await api.patch(`/kas-bank/estatements/rows/${encodeURIComponent(rowId)}`, payload);
  return data;
}

export async function bulkBreakdownRows(payload) {
  const { data } = await api.post("/kas-bank/estatements/rows/breakdown-bulk", payload);
  return data;
}

// 2026-02-13 (user request): Split Nominal + tanda "Tidak Breakdown".
/** Split 1 baris e-statement → 2..10 pecahan (total wajib = nominal asli). */
export async function splitEstatementRow(rowId, parts) {
  const { data } = await api.post(`/kas-bank/estatements/rows/${encodeURIComponent(rowId)}/split`, { parts });
  return data;
}

/** Batalkan split — pecahan digabung kembali; klasifikasi pecahan dihapus. */
export async function unsplitEstatementRow(rowId) {
  const { data } = await api.post(`/kas-bank/estatements/rows/${encodeURIComponent(rowId)}/unsplit`);
  return data;
}

/** Tandai/batalkan tanda "Tidak Breakdown" pada baris e-statement. */
export async function setNoBreakdown(rowId, excluded) {
  const { data } = await api.post(`/kas-bank/estatements/rows/${encodeURIComponent(rowId)}/no-breakdown`, { excluded });
  return data;
}

function _downloadBlob(res, fallbackName) {
  const dispo = res.headers?.["content-disposition"] || "";
  const m = dispo.match(/filename="?([^";]+)"?/);
  const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = m?.[1] || fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Export 1 e-statement (semua baris) → buku_bank_{bank}_{rek}_{periode}.xlsx */
export async function exportEstatement(estId) {
  const res = await api.get(`/kas-bank/estatements/${encodeURIComponent(estId)}/export.xlsx`, { responseType: "blob" });
  _downloadBlob(res, "buku_bank.xlsx");
}

/** Export baris yang SUDAH di-breakdown (1 sheet per rekening × periode). */
export async function exportBreakdownClassified(params = {}) {
  const q = new URLSearchParams();
  if (params.account_id) q.set("account_id", params.account_id);
  const res = await api.get(`/kas-bank/estatements/export-breakdown.xlsx${q.toString() ? "?" + q : ""}`, { responseType: "blob" });
  _downloadBlob(res, "breakdown.xlsx");
}

export async function getExportColumns() {
  const { data } = await api.get("/kas-bank/estatements/export-columns");
  return data?.columns || [];
}

export async function saveExportColumns(columns) {
  const { data } = await api.put("/kas-bank/estatements/export-columns", { columns });
  return data?.columns || [];
}

export async function getExportColors() {
  const { data } = await api.get("/kas-bank/estatements/export-colors");
  return data?.colors || null;
}

export async function saveExportColors(colors) {
  const { data } = await api.put("/kas-bank/estatements/export-colors", { colors });
  return data?.colors || null;
}

/** Export ledger Report Weekly Petty Cash (data persis seperti yang tampil di UI). */
export async function exportPettyCashReport(groups, grand) {
  const res = await api.post("/petty-cash/report/export.xlsx", { groups, grand }, { responseType: "blob" });
  _downloadBlob(res, "petty_cash_report.xlsx");
}

/** 2026-09-30 (user request): varian PDF — payload sudah terfilter (proyek/staff/tanggal/cari). */
export async function exportPettyCashReportPdf(groups, grand) {
  const res = await api.post("/petty-cash/report/export.pdf", { groups, grand }, { responseType: "blob" });
  _downloadBlob(res, "petty_cash_report.pdf");
}

export async function fetchTransferCandidates() {
  const { data } = await api.get("/kas-bank/estatements/transfer-candidates");
  return data || { pairs: [], pair_count: 0 };
}

export async function pairTransfer(payload) {
  const { data } = await api.post("/kas-bank/estatements/rows/pair-transfer", payload);
  return data;
}

/** Tanggal (Jakarta) hari ini utk default period. */
export function currentPeriodMonth() {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Jakarta" }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
